/**
 * boardIdentity — the Kanban surface's answer to "which board are we showing?",
 * expressed as a discriminated union instead of a string that sometimes is not
 * an id (SE-6491).
 *
 * The shape this replaces was `boardId={activeBoard?.id || "loading"}`. That
 * one expression collapsed four genuinely different situations into a single
 * string channel:
 *
 *   1. a real board is open (a genuine UUID),
 *   2. the board set has not resolved yet (unknown — ask again later),
 *   3. the board set resolved and this user has NO boards (a real, legitimate
 *      empty — nothing more will arrive),
 *   4. the board list could not be read at all — a query error, which on this
 *      surface includes an authorization refusal (a refusal, not an absence).
 *
 * Two concrete harms followed from the collapse. First, `"loading"` is truthy,
 * so every downstream `skip: !boardId` guard let it through and the literal
 * reached the server as a board id, where it is rejected as an invalid UUID —
 * a guaranteed `BAD_REQUEST_EXCEPTION` on load that polluted the API error
 * metrics alerting is built on. Second — and worse — the sentinel is not even
 * named after the state it actually marks: the screen only ever renders with
 * it once boards have finished loading and none exist, so a value spelled
 * "loading" was in practice the *absent* case, and case 4 was rendered
 * identically to case 3 — a failed or refused read shown to the user as a
 * confident "you have no boards".
 *
 * The union makes the four cases distinguishable in the TYPE, so a consumer
 * that forgets one fails to compile rather than rendering a plausible lie, and
 * an id only exists on the branch where an id exists.
 * @module features/player-kanban/utils/boardIdentity
 */

/**
 * Which board the Kanban surface is showing, and — when it is showing none —
 * why not. Exhaustive by construction: `kind` is the discriminant and
 * `boardId` exists on exactly one branch.
 */
export type BoardIdentity =
  /** A board is open. `boardId` is a real server id, safe to send. */
  | { readonly kind: "resolved"; readonly boardId: string }
  /**
   * Not known YET. The board list is in flight, or it has landed but no board
   * has been activated from it yet. Nothing may be requested by id, and the
   * surface must not claim the user has no boards — this state resolves.
   */
  | { readonly kind: "pending" }
  /**
   * Known to be empty. The board list resolved successfully and contains zero
   * boards. This is a real answer, not a missing one: the surface should offer
   * board creation rather than a spinner.
   */
  | { readonly kind: "absent" }
  /**
   * The board list could not be read — a network or GraphQL error, which on
   * this surface is also how an authorization refusal arrives. Distinct from
   * `absent` precisely because "we could not ask" must never be rendered as
   * "there is nothing".
   */
  | { readonly kind: "unavailable"; readonly reason: string };

/** Inputs `resolveBoardIdentity` needs, all sourced from `useBoardManager`. */
export type BoardIdentityInput = {
  /** The board-list query is in flight (or a board CRUD op is running). */
  readonly isLoadingBoards: boolean;
  /** The resolved board list; `undefined` until the first response lands. */
  readonly boards: readonly { readonly id: string }[] | undefined;
  /** The board the screen has activated, if any. */
  readonly activeBoard: { readonly id: string } | null | undefined;
  /** The board-list error message, if the read failed. */
  readonly boardsError: string | null | undefined;
};

/** Fallback copy when the board-list read fails without a usable message. */
export const BOARD_LIST_UNAVAILABLE_FALLBACK =
  "We could not load your boards. This may be a connection problem or you may not have access to this pipeline.";

/**
 * Resolve which of the four board-identity states the surface is in.
 *
 * Order matters and encodes the SE-5630 hazard: an ACTIVE board wins over
 * every not-yet/empty signal, so a board that is already on screen can never
 * be demoted to `pending` (and therefore unmounted) by a secondary signal
 * flapping. The only inputs that can produce a non-`resolved` answer are ones
 * evaluated when there is no active board at all.
 *
 * `unavailable` is deliberately checked LAST, after `boards.length > 0`: a
 * transient error alongside a populated list is a partial failure, not an
 * outage, and must not blank a usable surface. It can therefore only ever
 * replace the state that previously rendered as a silently empty board.
 * @param input - Board-list state from `useBoardManager`.
 * @returns The board identity, exhaustively discriminated on `kind`.
 */
export const resolveBoardIdentity = (
  input: BoardIdentityInput
): BoardIdentity => {
  const { isLoadingBoards, boards, activeBoard, boardsError } = input;

  if (activeBoard?.id) {
    return { kind: "resolved", boardId: activeBoard.id };
  }

  // No active board. Anything still in flight means "not yet", never "none".
  if (isLoadingBoards || boards === undefined) {
    return { kind: "pending" };
  }

  // Boards exist but none is active yet — activation is a follow-up tick, so
  // this is still "not yet", not an empty.
  if (boards.length > 0) {
    return { kind: "pending" };
  }

  if (boardsError) {
    return { kind: "unavailable", reason: boardsError };
  }

  return { kind: "absent" };
};

/**
 * The board id to put on the wire, or `undefined` when there is none.
 *
 * This is the seam that retires the `"loading"` sentinel: only the `resolved`
 * branch yields a string, so the three id-less states all collapse to
 * `undefined` — which every downstream `skip: !boardId` guard already reads
 * correctly, and which no server ever sees.
 * @param identity - The resolved board identity.
 * @returns A real board id, or `undefined` when no board is open.
 */
export const boardIdForQueries = (
  identity: BoardIdentity
): string | undefined =>
  identity.kind === "resolved" ? identity.boardId : undefined;

/**
 * What the surface currently HOLDS, as opposed to which board it wants.
 *
 * The second field is the whole of SE-5630: a loading flag on its own cannot
 * tell a cold start from a background refresh, and the gate that read only the
 * flag treated both as a reason to throw the board away.
 */
export type BoardContentState = {
  /**
   * Some query feeding the open board's content is in flight. This is an
   * AGGREGATE across the board read, the phase read, the card read and the
   * watchlist read, so it re-enters `true` for reasons that have nothing to do
   * with the board the user is looking at.
   */
  readonly isLoadingBoardContent: boolean;
  /**
   * The board id whose content (phases AND cards) is currently in hand, or
   * `null`/`undefined` when none is. Non-null means there is something real to
   * keep on screen; it changes to `null` the moment the content belongs to no
   * board, which is exactly what a board SWITCH produces.
   */
  readonly loadedContentBoardId: string | null | undefined;
};

/**
 * Whether the surface should render its loading skeleton.
 *
 * Split out so the "which board" question and the "is its content ready"
 * question stay separate: a `resolved` identity whose CONTENT is still loading
 * is the only case where an id exists and a skeleton might still show.
 *
 * SE-5630: "might" is the correction. The gate used to be all-or-nothing —
 * `resolved && isLoadingBoardContent` — and `isLoadingBoardContent` aggregates
 * five queries, so ANY of them re-entering loading (a card refetch after a
 * mutation, a phase refetch after a stage edit, a watchlist read, a bounded
 * board-read retry) unmounted the entire surface: columns, cards, TopPanel and
 * the view toggle all disappeared behind a full-screen spinner, and after 45s
 * behind a timeout card. Under a slow backend that is a user who HAD a working
 * board losing it; in CI it is a flake that presents as a rendering bug in
 * whatever feature happens to be under test.
 *
 * The gate now asks whether there is anything worth keeping. Content already
 * in hand for THIS board wins over the loading flag, so a background refresh
 * leaves the board mounted. When the content in hand belongs to a different
 * board — or to no board, which is what a switch and a cold start both look
 * like — there is nothing to preserve and the skeleton is the honest answer.
 *
 * Note what is deliberately NOT done: keeping the previous board's columns on
 * screen while a newly-selected board loads would be showing board A's players
 * under board B's name. Stale-but-usable is only a virtue when the staleness
 * is about time, not about identity.
 * @param identity - The resolved board identity.
 * @param content - What the surface currently holds, and whether more is coming.
 * @returns True when the skeleton, not the board, should render.
 */
export const shouldShowBoardSkeleton = (
  identity: BoardIdentity,
  content: BoardContentState
): boolean => {
  switch (identity.kind) {
    case "pending":
      return true;
    case "absent":
    case "unavailable":
      // Both are settled answers with their own dedicated UI. Spinning on
      // either is the blank-board dead-end SE-6491 came from.
      return false;
    case "resolved":
      return (
        content.isLoadingBoardContent &&
        content.loadedContentBoardId !== identity.boardId
      );
  }
};
